import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnChanges,
  OnDestroy,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { DayCheck } from '@qaroni-app/check/types/month-check';
import { DaySchedule } from '@qaroni-app/check/types/month-schedule';
import { Day } from '@qaroni-app/check/types/period';
import { Chart, TooltipItem, registerables } from 'chart.js';

@Component({
  selector: 'qaroni-hours-balance-chart-preview',
  templateUrl: './hours-balance-chart-preview.component.html',
  styleUrls: ['./hours-balance-chart-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HoursBalanceChartPreviewComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  // TODO: Remove Day interface when change endpoints on checks from admin
  readonly dayChecks = input<DayCheck[] | Day[]>([]);
  readonly daySchedules = input<DaySchedule[]>([]);
  readonly monthName = input<string>('');

  @ViewChild('hoursBalanceChart')
  hoursBalanceChart!: ElementRef<HTMLCanvasElement>;

  private zone = inject(NgZone);

  private chart: Chart | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private labels: string[] = [];
  private hours: number[] = [];
  private schedules: number[] = [];

  constructor() {
    Chart.register(...registerables);
  }

  ngOnChanges(): void {
    this.zone.runOutsideAngular(() => {
      this.initialize();
      if (this.chart) this.setUpChart();
    });
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.canvas = this.hoursBalanceChart.nativeElement;
      this.setUpChart();
    });
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      this.chart.destroy();
    });
  }

  private initialize = (): void => {
    const dayChecks = this.dayChecks();
    this.labels = dayChecks?.map(day => `${day.dayNumber} ${this.monthName()}`);
    this.hours = dayChecks?.map(day =>
      parseFloat((day.totalMinutes / 60).toFixed(2))
    );
    this.schedules = this.daySchedules()?.map(schedule =>
      parseFloat((schedule.scheduleTime / 60).toFixed(2))
    );
  };

  private setUpChart = (): void => {
    if (this.chart) this.chart.destroy();

    this.chart = new Chart(this.canvas, {
      plugins: [],
      type: 'bar',
      data: {
        labels: this.labels,
        datasets: [
          {
            label: $localize`Horas trabajadas`,
            data: this.hours,
            borderColor: '#000000',
            backgroundColor: '#8dec85',
          },
          {
            label: $localize`Horas esperadas`,
            data: this.schedules,
            borderColor: '#000000',
            backgroundColor: '#82c4f8',
          },
        ],
      },
      options: {
        responsive: true,
        layout: {
          // padding: {
          //   left: 15,
          //   right: 15,
          // },
        },
        plugins: {
          legend: {
            position: 'top',
            display: false,
          },
          // tooltip: {
          //   titleAlign: 'center',
          //   titleFont: {
          //     size: 11,
          //   },
          //   bodyFont: {
          //     size: 11,
          //   },
          //   footerFont: {
          //     size: 11,
          //   },
          //   callbacks: {
          //     footer: (tooltipItems) => {
          //       let diff: number = 0;
          //       tooltipItems.forEach((tooltipItem) => {
          //         diff =
          //           tooltipItem.parsed._stacks.y[0] -
          //           tooltipItem.parsed._stacks.y[1];
          //       });
          //       const diffText: string =
          //         diff > 0 ? `+${diff.toFixed(2)}` : `${diff.toFixed(2)}`;
          //       return (
          //         $localize`Diferencia` +
          //         `: ${diffText.replace('.', ',')} ` +
          //         $localize`horas`
          //       );
          //     },
          //   },
          // },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        scales: {
          x: {
            title: {
              display: true,
              text: $localize`Días del mes de ${this.monthName()}`,
              font: {
                weight: 'bold',
              },
            },
            stacked: false,
            display: false,
          },
          y: {
            title: {
              display: true,
              text: $localize`Horas`,
              font: {
                weight: 'bold',
              },
            },
            stacked: false,
            display: false,
          },
        },
      },
    });
  };

  private getDiffTooltip = (tooltipItems: TooltipItem<'bar'>[]): string => {
    let diff: number = 0;
    tooltipItems.forEach(tooltipItem => {
      diff = tooltipItem.parsed._stacks.y[0] - tooltipItem.parsed._stacks.y[1];
    });
    const diffText: string =
      diff > 0 ? `+${diff.toFixed(2)}` : `${diff.toFixed(2)}`;
    return (
      $localize`Diferencia` +
      `: ${diffText.replace('.', ',')} ` +
      $localize`horas`
    );
  };
}
